import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import SEO from "@/components/SEO";
import { PageHeader, Section, Reveal, Eyebrow } from "@/components/Section";
import ReleaseCard from "@/components/ReleaseCard";
import Mandala from "@/components/Mandala";
import { useMusic } from "@/context/MusicPlayer";

const FESTIVALS = {
  "hanuman-jayanti": {
    name: "Hanuman Jayanti",
    hindi: "हनुमान जयंती",
    tagline: "The birth of Bajrang Bali. The fire of Ram Bhakt.",
    description: "Celebrate Hanuman Jayanti with KASHiX — powerful Hanuman Chalisa, Hanuman Ashtak and Bajrang Baan renditions engineered for temple, workout and meditation.",
    category: "hanuman",
    accent: "#FF9933",
    hero: "https://images.unsplash.com/photo-1626544827763-d516dce335e2?w=1600&q=80",
  },
  "mahashivratri": {
    name: "Mahashivratri",
    hindi: "महाशिवरात्रि",
    tagline: "The night of Shiva. The Tandav that stills the universe.",
    description: "Enter Mahashivratri with KASHiX — Shiv Tandav Stotram, Shiv Chalisa, Shivashtakam, Kaal Bhairav and Mahamrityunjay chants in cinematic modern production.",
    category: "shiva",
    accent: "#FFD700",
    hero: "https://images.unsplash.com/photo-1548013146-72479768bada?w=1600&q=80",
  },
  "ram-navami": {
    name: "Ram Navami",
    hindi: "राम नवमी",
    tagline: "Jai Shri Ram. The eternal name.",
    description: "Ram Navami with KASHiX — devotional chants, meditative jaap, and the eternal name of Ram woven into a modern cinematic soundscape.",
    category: "bhajan",
    accent: "#FF9933",
    hero: "https://images.unsplash.com/photo-1517832606299-7ae9b720a186?w=1600&q=80",
  },
  "navratri": {
    name: "Navratri",
    hindi: "नवरात्रि",
    tagline: "Nine nights of Devi. Nine chapters of Shakti.",
    description: "Navratri with KASHiX — devotional chants for the nine forms of the Goddess, engineered with cinematic depth for prayer and celebration.",
    category: "bhajan",
    accent: "#FF9933",
    hero: "https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=1600&q=80",
  },
  "diwali": {
    name: "Diwali",
    hindi: "दीपावली",
    tagline: "The festival of light. The return of dharma.",
    description: "Diwali with KASHiX — meditative and celebratory devotional music to light the inner and outer temples.",
    category: "meditation",
    accent: "#FFD700",
    hero: "https://images.unsplash.com/photo-1550053808-52a75a05955d?w=1600&q=80",
  },
};

const Festival = () => {
  const { slug } = useParams();
  const cfg = FESTIVALS[slug];
  const [items, setItems] = useState([]);
  const { play } = useMusic();

  useEffect(() => {
    if (!cfg) return;
    http.get("/releases", { params: { category: cfg.category } }).then((r) => setItems(r.data));
  }, [cfg]);

  const jsonLd = useMemo(() => (cfg ? {
    "@context": "https://schema.org",
    "@type": "Event",
    name: `${cfg.name} — KASHiX`,
    description: cfg.description,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
    performer: { "@type": "MusicGroup", name: "KASHiX" },
    location: { "@type": "VirtualLocation", url: typeof window !== "undefined" ? window.location.href : "" },
  } : null), [cfg]);

  if (!cfg) return <Navigate to="/releases" replace />;

  return (
    <>
      <SEO
        title={`${cfg.name} · KASHiX`}
        description={cfg.description}
        image={cfg.hero}
        jsonLd={jsonLd}
      />
      <section className="relative pt-40 pb-14 overflow-hidden">
        <div className="absolute inset-0"
             style={{ backgroundImage: `url(${cfg.hero})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(40px) saturate(120%)", opacity: 0.35 }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/80 to-black" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="spin-slow opacity-25"><Mandala size={720} /></div>
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <Eyebrow>Festival Chapter</Eyebrow>
          <div className="mt-4 font-hindi text-4xl md:text-5xl" style={{ color: cfg.accent }}>{cfg.hindi}</div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-2 font-display text-5xl md:text-7xl tracking-tight text-white saffron-text-glow"
          >
            {cfg.name}
          </motion.h1>
          <p className="mt-4 text-white/80 text-lg max-w-3xl font-editorial italic">{cfg.tagline}</p>
          <p className="mt-3 text-white/60 max-w-3xl">{cfg.description}</p>
          {items[0] && (
            <div className="mt-8 flex items-center gap-3">
              <button
                onClick={() => play(items[0], items)}
                className="inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] hover:shadow-[0_0_30px_rgba(255,153,51,0.55)] transition-all"
              >
                <Play size={12} fill="currentColor" /> Play the Chapter
              </button>
              <Link to="/releases" className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white px-5 py-3 text-xs uppercase tracking-[0.3em] hover:text-[#FF9933] hover:border-[#FF9933] transition">
                Explore full catalog <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      </section>

      <Section className="!py-16">
        <Reveal>
          <Eyebrow>The {cfg.name} chapter</Eyebrow>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-white tracking-tight">
            Curated devotional music for {cfg.name}
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((r, i) => <ReleaseCard key={r.id} release={r} index={i} list={items} />)}
        </div>
      </Section>

      <Section className="!py-16">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl glass-strong p-10 text-center">
            <h3 className="font-display text-3xl md:text-4xl text-white tracking-tight">Bless the next chapter.</h3>
            <p className="mt-4 max-w-lg mx-auto text-white/70">Every ₹ becomes studio time for the next devotional release.</p>
            <Link to="/support" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] transition">
              Support KASHiX
            </Link>
          </div>
        </Reveal>
      </Section>
    </>
  );
};

export default Festival;
